import { Table, Button, Modal, Input, Tag, message } from "antd";
import { useState, useContext, useEffect } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

export default function GetStatusChange() {
    const { authData, backUrl } = useContext(AuthContext);
    const [statusChanges, setStatusChanges] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [activeStatus, setActiveStatus] = useState("PENDING");

    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [adminRemarks, setAdminRemarks] = useState("");
    const [selectedRecord, setSelectedRecord] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/employee-status-change/`, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            setStatusChanges(res.data.results || res.data || []);
        } catch (error) {
            console.error("Error fetching status changes:", error.response || error.message);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const statusColors = {
        Pending: "orange",
        Approved: "green",
        Rejected: "red",
        Cancelled: "gray",
        RESIGNED: "volcano",
        TERMINATED: "red",
        ABSCONDED: "magenta",
    };

    const handleApprove = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/employee-status-change/${selectedRecord.id}/approve/`,
                { admin_remarks: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "Status change approved successfully.");
            setIsApproveModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
        } catch (error) {
            console.error("Error approving status change:", error);
            message.error(error.response?.data?.message || "Failed to approve status change.");
        }
    };

    const handleReject = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/employee-status-change/${selectedRecord.id}/reject/`,
                { admin_remarks: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "Status change rejected successfully.");
            setIsRejectModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
        } catch (error) {
            console.error("Error rejecting status change:", error);
            message.error(error.response?.data?.message || "Failed to reject status change.");
        }
    };

    // Filter by approval_status
    const filteredData = statusChanges.filter(
        (item) => item.approval_status === activeStatus
    );

    // Count badges per status
    const counts = {
        Pending: statusChanges.filter((i) => i.approval_status === "PENDING").length,
        Approved: statusChanges.filter((i) => i.approval_status === "APPROVED").length,
        Rejected: statusChanges.filter((i) => i.approval_status === "REJECTED").length,
    };

    const statusButtons = [
        { label: "PENDING", color: "#f5a623", activeColor: "#d4880e" },
        { label: "APPROVED", color: "#52c41a", activeColor: "#389e0d" },
        { label: "REJECTED", color: "#ff4d4f", activeColor: "#cf1322" },
    ];

    const columns = [
        {
            title: "Employee",
            key: "employee",
            render: (_, record) => {
                const user = record?.employee_details?.user;
                return user ? `${user.first_name} ${user.last_name}` : "-";
            }
        },
        {
            title: "Requested Status",
            dataIndex: "requested_status",
            key: "requested_status",
            render: (text) => (
                <Tag color={statusColors[text] || "blue"}>{text || "-"}</Tag>
            ),
        },
        {
            title: "Date of Leaving",
            dataIndex: "date_of_leaving",
            key: "date_of_leaving",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Last Working Day",
            dataIndex: "last_working_day",
            key: "last_working_day",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Probation End Date",
            dataIndex: "probation_end_date",
            key: "probation_end_date",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Confirmation Date",
            dataIndex: "employee_confirmation_date",
            key: "employee_confirmation_date",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            width: 300,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "300px" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Current Status",
            dataIndex: ["employee_details", "status"],
            key: "status",
            render: (text) => (
                <Tag color={statusColors[text] || "default"}>{text || "-"}</Tag>
            ),
        },
        {
            title: "Admin Remarks",
            dataIndex: "admin_remarks",
            key: "admin_remarks",
            width: 300,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "300px" }}>
                    {text || "-"}
                </div>
            ),
        },
        // Show Actions column only for Pending tab
        ...(activeStatus === "PENDING"
            ? [
                {
                    title: "Actions",
                    key: "actions",
                    render: (_, record) => (
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                                type="text"
                                style={{ color: "green" }}
                                icon={<CheckOutlined />}
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setIsApproveModalVisible(true);
                                }}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setIsRejectModalVisible(true);
                                }}
                            />
                        </div>
                    ),
                },
            ]
            : []),
    ];

    return (
        <>
            {/* Approve Modal */}
            <Modal
                title="Approve Status Change"
                open={isApproveModalVisible}
                onOk={handleApprove}
                onCancel={() => {
                    setIsApproveModalVisible(false);
                    setAdminRemarks("");
                    setSelectedRecord(null);
                }}
                okText="Approve"
                cancelText="Close"
                okButtonProps={{ style: { backgroundColor: "green", borderColor: "green" } }}
            >
                <p>
                    Are you sure you want to <strong>approve</strong> this status change request
                    {selectedRecord?.id ? ` (ID: ${selectedRecord.id})` : ""}?
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter admin remarks"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* Reject Modal */}
            <Modal
                title="Reject Status Change"
                open={isRejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setIsRejectModalVisible(false);
                    setAdminRemarks("");
                    setSelectedRecord(null);
                }}
                okText="Reject"
                cancelText="Close"
                okButtonProps={{ danger: true }}
            >
                <p>
                    Are you sure you want to <strong>reject</strong> this status change request
                    {selectedRecord?.id ? ` (ID: ${selectedRecord.id})` : ""}?
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter rejection remarks"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* Status Filter Toggle Buttons */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "20px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                }}
            >
                {statusButtons.map(({ label, color, activeColor }) => (
                    <Button
                        key={label}
                        onClick={() => setActiveStatus(label)}
                        style={{
                            backgroundColor: activeStatus === label ? activeColor : "#f0f0f0",
                            color: activeStatus === label ? "#fff" : "#333",
                            borderColor: activeStatus === label ? activeColor : "#d9d9d9",
                            fontWeight: activeStatus === label ? 600 : 400,
                            minWidth: isMobile ? "100%" : 140,
                            transition: "all 0.2s ease",
                        }}
                    >
                        {label}{" "}
                    </Button>
                ))}
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={filteredData.map((item, index) => ({
                    key: index,
                    ...item,
                }))}
                pagination={{ pageSize: 5 }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </>
    );
}